/**
 * Yarnly Crochet — gallery-lightbox.js
 *
 * <yarnly-gallery-lightbox> (sections/product-gallery.liquid) enhances the
 * "What You'll Make" gallery with a native <dialog> lightbox.
 *
 * Markup contract
 *   <yarnly-gallery-lightbox data-section-id data-counter-template="[index] of [count]"
 *       data-position-template="Image [index] of [count]">
 *     a[data-lightbox-open][data-index]  one per image; a real link to the
 *         full-size image (the no-JS fallback) carrying data-lightbox-src,
 *         -srcset, -alt, -caption, -width, -height
 *     dialog[data-lightbox-dialog]
 *       [data-lightbox-dismiss]          the stage; a click on it (not on its
 *                                        children) counts as a backdrop click
 *       [data-lightbox-close] [data-lightbox-prev] [data-lightbox-next]
 *       img[data-lightbox-image] [data-lightbox-caption] [data-lightbox-counter]
 *       [data-lightbox-live]             visually-hidden aria-live="polite" region;
 *                                        announces "Image X of Y" plus the caption
 *                                        on every show() (translated, from
 *                                        data-position-template — no English in JS)
 *   </yarnly-gallery-lightbox>
 *
 * Behaviour: a trigger click opens the dialog (showModal, or the `open`
 * attribute plus the theme's focus trap where showModal is missing) on the
 * clicked image; prev/next buttons, ArrowLeft/ArrowRight and a horizontal
 * swipe move through the images (wrapping); Escape, the close button and a
 * backdrop click close it; focus returns to the trigger; page scroll is
 * locked while it is open; modal:open / modal:close are published so the
 * sticky bar steps aside. Strings arrive pre-translated in the markup.
 *
 * Plain ES2020 IIFE, safe to evaluate twice (the define is guarded).
 */
(function () {
  'use strict';

  const S = window.Yarnly;
  if (!S) return;

  const SWIPE_MIN = 48;

  function utils() {
    return S.utils || {};
  }

  function publish(name, payload) {
    if (S.pubsub && typeof S.pubsub.publish === 'function') S.pubsub.publish(name, payload);
  }

  function format(template, values) {
    const fn = utils().formatTemplate;
    if (typeof fn === 'function') return fn(template, values);
    return String(template || '').replace(/\[(\w+)\]/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match
    );
  }

  // Only the scroll lock this element owns: another open modal or drawer keeps
  // the page locked after the lightbox closes.
  function setScrollLock(locked) {
    const body = document.body;
    if (!body) return;
    if (locked) {
      body.classList.add('has-modal-open');
      return;
    }
    const otherModal = document.querySelector('yarnly-modal.is-open, yarnly-gallery-lightbox.is-open');
    const drawer = document.querySelector('yarnly-drawer.is-open');
    if (!otherModal) body.classList.remove('has-modal-open');
    if (!otherModal && !drawer) body.classList.remove('has-drawer-open');
  }

  if (!customElements.get('yarnly-gallery-lightbox')) {
    class YarnlyGalleryLightbox extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.isOpen = false;
        this.fallback = false;
        this.index = 0;
        this.trigger = null;
        this.touchX = null;
      }

      connectedCallback() {
        this.dialog = this.querySelector('[data-lightbox-dialog]');
        this.addEventListener('click', this.onClick);
        if (!this.dialog) return;
        this.dialog.addEventListener('keydown', this.onKeyDown);
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('close', this.onDialogClose);
        this.dialog.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.dialog.addEventListener('touchend', this.onTouchEnd);
        this.syncNavVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.dialog) {
          this.dialog.removeEventListener('keydown', this.onKeyDown);
          this.dialog.removeEventListener('cancel', this.onCancel);
          this.dialog.removeEventListener('close', this.onDialogClose);
          this.dialog.removeEventListener('touchstart', this.onTouchStart);
          this.dialog.removeEventListener('touchend', this.onTouchEnd);
        }
        // An editor re-render can remove the section while the lightbox is open.
        if (this.isOpen) {
          this.isOpen = false;
          this.classList.remove('is-open');
          if (this.fallback && typeof utils().removeTrapFocus === 'function') utils().removeTrapFocus();
          setScrollLock(false);
        }
      }

      get triggers() {
        return Array.from(this.querySelectorAll('[data-lightbox-open]'));
      }

      syncNavVisibility() {
        const single = this.triggers.length < 2;
        this.querySelectorAll('[data-lightbox-prev], [data-lightbox-next]').forEach((button) => {
          button.hidden = single;
        });
      }

      onClick(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        const trigger = target.closest('[data-lightbox-open]');
        if (trigger && this.contains(trigger) && !(this.dialog && this.dialog.contains(trigger))) {
          if (!this.dialog) return; // no dialog: let the link open the image
          event.preventDefault();
          this.open(trigger);
          return;
        }
        if (!this.isOpen || !this.dialog || !this.dialog.contains(target)) return;

        if (target.closest('[data-lightbox-close]')) {
          event.preventDefault();
          this.close();
        } else if (target.closest('[data-lightbox-prev]')) {
          event.preventDefault();
          this.step(-1);
        } else if (target.closest('[data-lightbox-next]')) {
          event.preventDefault();
          this.step(1);
        } else if (target === this.dialog || target.hasAttribute('data-lightbox-dismiss')) {
          // The <dialog> itself (its ::backdrop) or the empty stage around the image.
          this.close();
        }
      }

      onKeyDown(event) {
        if (!this.isOpen) return;
        switch (event.key) {
          case 'Escape':
          case 'Esc':
            event.preventDefault();
            this.close();
            break;
          case 'ArrowRight':
            event.preventDefault();
            this.step(1);
            break;
          case 'ArrowLeft':
            event.preventDefault();
            this.step(-1);
            break;
          default:
            break;
        }
      }

      // The platform's own Escape handling (showModal): route it through close()
      // so focus and the scroll lock are restored the same way.
      onCancel(event) {
        event.preventDefault();
        this.close();
      }

      // The dialog was closed by something else (a form[method=dialog], the browser).
      onDialogClose() {
        if (this.isOpen) this.finish();
      }

      onTouchStart(event) {
        const touch = event.changedTouches && event.changedTouches[0];
        this.touchX = touch ? touch.clientX : null;
      }

      onTouchEnd(event) {
        const touch = event.changedTouches && event.changedTouches[0];
        if (this.touchX === null || !touch) return;
        const delta = touch.clientX - this.touchX;
        this.touchX = null;
        if (Math.abs(delta) < SWIPE_MIN || this.triggers.length < 2) return;
        this.step(delta < 0 ? 1 : -1);
      }

      open(trigger) {
        const triggers = this.triggers;
        const index = Math.max(0, triggers.indexOf(trigger));
        this.trigger = trigger;
        this.show(index);
        if (this.isOpen) return;

        this.isOpen = true;
        this.classList.add('is-open');
        this.syncNavVisibility();
        this.fallback = typeof this.dialog.showModal !== 'function';
        if (!this.fallback) {
          try {
            if (!this.dialog.open) this.dialog.showModal();
          } catch (error) {
            this.fallback = true;
          }
        }
        const focusTarget = this.dialog.querySelector('[data-lightbox-close]') || this.dialog;
        if (this.fallback) {
          this.dialog.setAttribute('open', '');
          if (typeof utils().trapFocus === 'function') utils().trapFocus(this.dialog, focusTarget);
          else focusTarget.focus();
        } else {
          focusTarget.focus({ preventScroll: true });
        }
        setScrollLock(true);
        publish('modal:open', { id: this.dialog.id, modal: this, trigger });
        this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true, detail: { id: this.dialog.id } }));
      }

      step(direction) {
        const count = this.triggers.length;
        if (!count) return;
        this.show((this.index + direction + count) % count);
      }

      show(index) {
        const triggers = this.triggers;
        const trigger = triggers[index];
        if (!trigger || !this.dialog) return;
        this.index = index;

        const img = this.dialog.querySelector('[data-lightbox-image]');
        if (img) {
          const src = trigger.getAttribute('data-lightbox-src') || trigger.getAttribute('href') || '';
          const srcset = trigger.getAttribute('data-lightbox-srcset');
          const width = trigger.getAttribute('data-lightbox-width');
          const height = trigger.getAttribute('data-lightbox-height');
          // Clear srcset first so the browser never pairs the new src with the old set.
          img.removeAttribute('srcset');
          img.setAttribute('src', src);
          if (srcset) {
            img.setAttribute('srcset', srcset);
            img.setAttribute('sizes', '(min-width: 990px) 80vw, 100vw');
          }
          if (width && height) {
            img.setAttribute('width', width);
            img.setAttribute('height', height);
          } else {
            img.removeAttribute('width');
            img.removeAttribute('height');
          }
          img.setAttribute('alt', trigger.getAttribute('data-lightbox-alt') || '');
        }

        const caption = this.dialog.querySelector('[data-lightbox-caption]');
        if (caption) {
          const text = trigger.getAttribute('data-lightbox-caption') || '';
          caption.textContent = text;
          caption.hidden = !text;
        }

        const counter = this.dialog.querySelector('[data-lightbox-counter]');
        if (counter) {
          const template = this.getAttribute('data-counter-template') || '[index] / [count]';
          counter.textContent = triggers.length > 1 ? format(template, { index: index + 1, count: triggers.length }) : '';
        }

        const live = this.dialog.querySelector('[data-lightbox-live]');
        if (live) {
          const positionTemplate = this.getAttribute('data-position-template') || '[index] / [count]';
          let announcement = format(positionTemplate, { index: index + 1, count: triggers.length });
          const captionText = trigger.getAttribute('data-lightbox-caption') || '';
          if (captionText) announcement += '. ' + captionText;
          live.textContent = announcement;
        }
      }

      close() {
        if (!this.isOpen || !this.dialog) return;
        this.isOpen = false; // before dialog.close(), so the close event is a no-op
        if (!this.fallback && this.dialog.open && typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
        this.finish();
      }

      finish() {
        this.isOpen = false;
        this.classList.remove('is-open');
        if (this.fallback && typeof utils().removeTrapFocus === 'function') utils().removeTrapFocus();
        setScrollLock(false);
        const trigger = this.trigger;
        this.trigger = null;
        if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
          trigger.focus({ preventScroll: true });
        }
        publish('modal:close', { id: this.dialog.id, modal: this });
        this.dispatchEvent(new CustomEvent('modal:close', { bubbles: true, detail: { id: this.dialog.id } }));
      }
    }

    customElements.define('yarnly-gallery-lightbox', YarnlyGalleryLightbox);
  }
  S.GalleryLightbox = customElements.get('yarnly-gallery-lightbox');
})();
